import { useApolloClient } from '@apollo/client';
import { useProjectDirectories } from '@/context/ProjectDirectoriesContext';
import { detectPrimaryDAW } from '@/common/utils/fileUtils';
import { useOpenProjectInDAW } from '@/common/components/projectDetails/hooks/useOpenProjectInDAW';
import { OpenProjectInDAWButton } from '@/common/components/projectDetails/components/OpenProjectInDAWButton';
import {
  GetProjectLibraryFilesDocument,
  type GetProjectLibraryFilesQueryData,
  type GetProjectLibraryFilesQueryVariables,
  type GetProjectsQueryData,
} from '@/generated/graphql';

export function ProjectListLaunchButton({
  project,
  tabIndex,
  isStem = false,
}: {
  project: GetProjectsQueryData['projects'][number];
  tabIndex: number;
  isStem?: boolean;
}) {
  const client = useApolloClient();
  const { getStoredProjectPath } = useProjectDirectories();
  const projectDirPath = getStoredProjectPath(project._id);
  const version = Math.max(
    0,
    ...(project.versionHistory || []).map((entry) => entry.version)
  );
  const { handleOpenProject } = useOpenProjectInDAW(async () => {
    // Do not fetch every project's files on page load or open its detail dialog.
    const { data } = await client.query<
      GetProjectLibraryFilesQueryData,
      GetProjectLibraryFilesQueryVariables
    >({
      query: GetProjectLibraryFilesDocument,
      variables: { projectId: project._id, version },
      fetchPolicy: 'network-only',
    });
    const latestVersionFiles = data?.projectVersion?.files || [];
    return {
      projectDirPath,
      latestVersionFiles,
      primaryDAW: detectPrimaryDAW(latestVersionFiles),
    };
  });

  const disabledReason = isStem
    ? 'Stem projects do not have a DAW project file to launch.'
    : !version
      ? 'Upload a project version before launching.'
      : !projectDirPath
        ? 'Open project details and locate the local project folder first.'
        : undefined;

  return (
    <OpenProjectInDAWButton
      onOpen={handleOpenProject}
      label={`Launch ${project.name} in DAW`}
      disabledReason={disabledReason}
      tabIndex={tabIndex}
    />
  );
}
